import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

async function loadPdf(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    try {
        return await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

/**
 * Group positioned PDF text items into spreadsheet rows and columns.
 * @param {import('pdfjs-dist').TextItem[]} items
 */
function itemsToRows(items, yTolerance = 5, xGap = 10) {
    const positioned = items
        .filter((item) => item.str && item.str.trim())
        .map((item) => ({
            text: item.str.trim(),
            x: item.transform[4],
            y: item.transform[5],
            width: item.width || item.str.length * 4,
        }));

    if (!positioned.length) return [];

    const rows = [];
    for (const item of positioned) {
        let row = rows.find((r) => Math.abs(r.y - item.y) <= yTolerance);
        if (!row) {
            row = { y: item.y, cells: [] };
            rows.push(row);
        }
        row.cells.push(item);
    }

    rows.sort((a, b) => b.y - a.y);

    return rows.map((row) => {
        row.cells.sort((a, b) => a.x - b.x);
        const merged = [];
        for (const cell of row.cells) {
            const last = merged[merged.length - 1];
            if (last && cell.x - (last.x + last.width) < xGap) {
                last.text = `${last.text} ${cell.text}`.trim();
                last.width = Math.max(last.width, cell.x + cell.width - last.x);
            } else {
                merged.push({ ...cell });
            }
        }
        return merged.map((cell) => cell.text);
    });
}

function normalizeRows(rows) {
    const maxCols = rows.reduce((max, row) => Math.max(max, row.length), 0);
    if (!maxCols) return [['']];
    return rows.map((row) => {
        const next = [...row];
        while (next.length < maxCols) next.push('');
        return next;
    });
}

async function extractPageRows(page) {
    const textContent = await page.getTextContent();
    return normalizeRows(itemsToRows(textContent.items));
}

async function buildWorkbookBlob(pagesData, sheetMode) {
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();

    if (sheetMode === 'perPage') {
        pagesData.forEach((rows, index) => {
            const sheet = XLSX.utils.aoa_to_sheet(rows);
            const name = `Page ${index + 1}`.slice(0, 31);
            XLSX.utils.book_append_sheet(workbook, sheet, name);
        });
    } else {
        const combined = [];
        pagesData.forEach((rows, index) => {
            if (index > 0) combined.push([]);
            combined.push([`Page ${index + 1}`]);
            combined.push(...rows);
        });
        const sheet = XLSX.utils.aoa_to_sheet(combined.length ? combined : [['']]);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Data');
    }

    const bytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}

/**
 * Convert a text-based PDF to an Excel workbook.
 * @param {File} file
 * @param {{ sheetMode: 'perPage'|'single' }} options
 * @param {(page: number, total: number) => void} [onProgress]
 */
export async function convertPdfToExcel(file, options, onProgress) {
    const pdf = await loadPdf(file);
    const total = pdf.numPages;
    const pagesData = [];
    let hasText = false;

    for (let i = 1; i <= total; i++) {
        onProgress?.(i, total);
        const page = await pdf.getPage(i);
        const rows = await extractPageRows(page);
        if (rows.some((row) => row.some((cell) => String(cell).trim()))) {
            hasText = true;
        }
        pagesData.push(rows);
    }

    if (!hasText) throw new Error('noText');

    const blob = await buildWorkbookBlob(pagesData, options.sheetMode || 'perPage');
    return blob;
}
